<script setup lang="ts">
import { computed, ref } from "vue";
import { copyTextToClipboard } from "../clipboard";
import type { Artifact } from "../types";

const props = defineProps<{ artifact: Artifact }>();
const expanded = ref(false);

const normalizedPath = computed(() => props.artifact.name.replaceAll("\\", "/"));
const filename = computed(() => normalizedPath.value.split("/").filter(Boolean).at(-1) || props.artifact.name);
const candidates = computed(() => [...props.artifact.urls].sort((left, right) => left.priority - right.priority || left.id - right.id));
const primaryUrl = computed(() => candidates.value[0]?.url || "");
const alternateUrls = computed(() => candidates.value.slice(1));
const availabilityLabel = computed(() => Number(props.artifact.size || 0) > 0 ? "可用" : "链接失效");
const checksum = computed(() => props.artifact.checksum_value || "—");
const primaryLabel = computed(() => alternateUrls.value.length ? "官方入口" : "打开");
const copyLabel = computed(() => alternateUrls.value.length ? "复制官方入口" : "复制链接");

function formatBytes(value: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(unit ? 2 : 0)} ${units[unit]}`;
}

async function copyPrimary(): Promise<void> {
  await copyTextToClipboard(primaryUrl.value);
}
</script>

<template>
  <button class="browser-row file-row fragment-file-row" :class="{ selected: expanded }" type="button" @click="expanded = !expanded">
    <span class="browser-icon file"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3h8l4 4v14H6zM14 3v5h5" /></svg></span>
    <span class="browser-name"><strong>{{ filename }}</strong><small>{{ checksum }}</small></span>
    <span class="browser-size">{{ formatBytes(artifact.size) }}</span>
  </button>
  <div v-if="expanded" class="browser-detail fragment-file-detail">
    <div><strong>{{ availabilityLabel }} / {{ normalizedPath }}</strong><span># {{ checksum }}</span></div>
    <div v-if="primaryUrl" class="file-actions fragment-file-actions">
      <button class="icon-button" type="button" @click="copyPrimary"><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>{{ copyLabel }}</span></button>
      <a class="icon-button" :href="primaryUrl" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>{{ primaryLabel }}</span></a>
      <a v-for="(candidate, index) in alternateUrls" :key="candidate.id" class="icon-button mirror-link" :href="candidate.url" target="_blank" rel="noreferrer"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></svg><span>CDN{{ index + 2 }}</span></a>
    </div>
  </div>
</template>
